import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, Renderer2, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import QRCodeStyling from 'qr-code-styling';
import { BehaviorSubject, Subscription, timer } from 'rxjs';

interface QRData {
  version: number;
  orderId: number;
  mineId: number;
  userId: number;
  lat: number;
  lng: number;
  epoch?: number;
  nonce: string;
}

@Component({
  selector: 'lohi-mine-qr-generator',
  templateUrl: './mine-qr-generator.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MineQrGeneratorComponent implements OnDestroy {
  public jsonFc = new FormControl(`{
  "version": 1,
  "orderId": 2384601,
  "mineId": 919,
  "userId": "LUSPOYS36V3",
  "lng": -104.99025,
  "lat": 39.739235,
  "epoch": 1659398185,
  "nonce": "dc4fa87a-d74c-44ea-b504-b2f76047a6c9"
}`);
  private sub: Subscription;
  @ViewChild('qrContainer', { static: true }) public qrContainer: ElementRef<HTMLDivElement>;
  public qrText$$ = new BehaviorSubject<string | null>(null);

  constructor(private renderer: Renderer2) {
    this.getQrCode();
  }

  public ngOnDestroy() {
    if (this.sub && !this.sub.closed) {
      this.sub.unsubscribe();
    }
  }

  public async getQrCode() {
    const qrData: QRData = JSON.parse(this.jsonFc.value);
    if (this.sub && !this.sub.closed) {
      this.sub.unsubscribe();
    }
    this.sub = timer(0, 1000).subscribe({
      next: async () => {
        let epochSeconds = qrData.epoch;
        if (!qrData.epoch) {
          const now = new Date().getTime(); // returns epoch milliseconds
          epochSeconds = Math.round(now / 1000);
        }
        // aiming for this
        // prefix|1|6015857|62361|124568|-99.3238439338684|31.1279069650532|1658449103.c0b231a327079052ed93d8c816ba862cda14e353401ee8d5af2e4476bc4b44b9
        // MINEQR|v|orderid| uid |mineid| lat             | lng            | epoch    . hs256 of the plaintext stuff using nonce
        // v is the version number. this will be HARD CODED to "1" right now but if we ever need to change this, we can
        const qrBody = `MINEQR|${qrData.version}|${qrData.orderId}|${qrData.mineId}|${qrData.userId}|${qrData.lng}|${qrData.lat}|${epochSeconds}`;
        // HMAC signature using the ENTIRE qrBody above and the nonce on the order (key qrNonce)
        const signature = await this.hmacSha256Hex(qrData.nonce, qrBody);
        // full qr text works a lot like a JWT, the body and the signature separated by a dot
        const fullQRText = `${qrBody}.${signature}`;
        this.qrText$$.next(fullQRText);
        this.populateQrCode(fullQRText);
      },
    });
  }

  private async hmacSha256Hex(secret, message) {
    const enc = new TextEncoder();
    const algorithm = { name: 'HMAC', hash: 'SHA-256' };
    const key = await crypto.subtle.importKey('raw', enc.encode(secret), algorithm, false, ['sign', 'verify']);
    const hashBuffer = await crypto.subtle.sign(algorithm.name, key, enc.encode(message));
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  }

  private populateQrCode(data: string) {
    const qrCode = new QRCodeStyling({
      width: 300,
      height: 300,
      type: 'svg',
      data,
      margin: 0,
      qrOptions: {
        typeNumber: 0,
        mode: 'Byte',
        errorCorrectionLevel: 'M',
      },
      backgroundOptions: {
        color: '#ffffff',
      },
      cornersSquareOptions: {
        color: '#000000',
      },
      cornersDotOptions: {
        color: '#000000',
      },
    });
    while (this.qrContainer.nativeElement.firstChild) {
      this.qrContainer.nativeElement.removeChild(this.qrContainer.nativeElement.firstChild);
    }
    qrCode.append(this.qrContainer.nativeElement);
    this.renderer.setAttribute(this.qrContainer.nativeElement.lastChild, 'viewBox', '0 0 300 300');
    this.renderer.setAttribute(this.qrContainer.nativeElement.lastChild, 'height', '300px');
    this.renderer.removeAttribute(this.qrContainer.nativeElement.lastChild, 'width');
  }
}
